/** @format */
/* eslint-disable @nx/enforce-module-boundaries */
import { Checkout, CheckoutError, ShipModeGroup } from '@cart-checkout/data-access/interfaces';
import {
  CheckoutAddress,
  CheckoutPayment,
  CommerceAddress,
  ICartItem,
} from '@shared/data-access/interfaces';

import { AnalyticsService } from '../analytics.service';
import { CheckoutErrorLogKeys } from '@cart-checkout/data-access/enums';
import { Injectable } from '@angular/core';
import { LoggerLevel } from '../enums/logger-level.enum';

@Injectable({
  providedIn: 'root',
})
export class CheckoutAnalyticsService extends AnalyticsService {
  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setCheckoutStep
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param {string} step - The current step of checkout
   *
   * @description
   * Sets the step of checkout the user is currently on.
   */
  public setCheckoutStep(step: string): void {
    this.window.siteData.CheckoutStep = step;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setBasePropsForCart
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param {string} step - The current step of checkout
   *
   * @description
   * Sets the step of checkout the user is currently on.
   */
  public setBasePropsForCart(
    checkout: any,
    IcartItems?: ICartItem[],
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    cartEditEnabled = false,
  ): void {
    // if checkout.cart is null or undefined we failed to reach out to elastic
    // and want to avoid this logic to not break our page
    if (checkout.cart != null) {
      this.window.siteData.CartID = checkout.cart.external_key;
      this.window.siteData.OrderID = checkout.cart.external_key;
      this.window.siteData.PaypalAvailable = checkout.cart?.paypal_eligible;
      // Totals
      this.window.siteData.MerchandiseSubtotal = checkout.pricing.total_unit;
      this.window.siteData.ShippingTotal = checkout.pricing.total_shipping;
      this.window.siteData.TaxTotal = checkout.pricing.total_tax;
      this.window.siteData.TotalOrderDiscount = checkout.pricing.total_discounts;
      this.window.siteData.OrderTotal = checkout.pricing.total;
      this.window.siteData.ShippingDiscountAmount = this.calculateShippingDiscount(
        checkout.cart.items,
      );
      // Products
      this.window.siteData.ProductArray = this.createProductArray(checkout);
      // Promotions
      this.setOrderLvlPromoData(checkout.order_summary);
    }
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setBasePropsForCheckout
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param {string} step - The current step of checkout
   *
   * @description
   * Sets the step of checkout the user is currently on.
   */
  public setBasePropsForCheckout(checkout: Checkout): void {
    // if checkout.cart is null or undefined we failed to reach out to elastic
    // and want to avoid this logic to not break our page
    if (checkout.cart != null) {
      this.window.siteData.CartID = checkout.cart.external_key;
      this.window.siteData.OrderID = checkout.cart.external_key;
      this.window.siteData.UserID = checkout.shopper?.external_key;
      this.window.siteData.PaypalAvailable = checkout.cart?.paypal_eligible;
      this.window.siteData.ShippingMethod = this.getShippingMethods(
        checkout.shipping?.selected?.groups,
      );
      this.window.siteData.PaymentType = this.getPaymentTypesFromPayment(checkout.payment);
      this.window.siteData.ScoreCardApplied = checkout.score_card ? 'true' : 'false';

      // Totals
      this.window.siteData.MerchandiseSubtotal = checkout.pricing.total_unit;
      this.window.siteData.ShippingTotal = checkout.pricing.total_shipping;
      this.window.siteData.TaxTotal = checkout.pricing.total_tax;
      this.window.siteData.TotalOrderDiscount = checkout.pricing.total_discounts;
      this.window.siteData.OrderTotal = checkout.pricing.total;
      this.window.siteData.ShippingDiscountAmount = this.calculateShippingDiscount(
        checkout.cart.items,
      );

      // Products
      this.window.siteData.ProductArray = this.createProductArray(checkout);

      // Promotions
      this.setOrderLvlPromoData(checkout.order_summary);

      // Billing Address
      const commerceAddress: CommerceAddress = this.getCommerceAddress(checkout.addresses);
      if (commerceAddress) {
        window.siteData.BillingState = commerceAddress.state;
        window.siteData.BillingZip = commerceAddress.zipcode;
      }
    }
  }

  public setWarrantyPropsForCheckout(checkout: Checkout): void {
    if (!checkout.cart || !this.window.siteData.ProductArray) return;

    for (const [index, item] of checkout.cart.items.entries()) {
      const availableWarranties = Array.isArray(item.availableWarranties)
        ? item.availableWarranties
        : [];

      if (!item.availableWarranties?.length) continue;

      const warrantiesWithMessage = availableWarranties.filter(
        (warranty) => typeof warranty.message === 'string',
      );

      warrantiesWithMessage.forEach((availableWarrantySelected) => {
        this.triggerAnalyticsEvent('NonMerchMediator', {
          ProductID: item.ecode,
          ProductSKU: item.sku,
          ActionReason: 'Warranty',
          WarrantyMessage: availableWarrantySelected.message,
        });
      });

      const warrantyAddons = item.addons?.filter((addon) => addon.type === 'warranty');

      if (!warrantyAddons?.length) continue;

      const productArrayItem =
        this.window.siteData.ProductArray.find((product) => product.ProductSKU === item.sku) ??
        this.window.siteData.ProductArray[index];

      let warrantiesQtyTotal = 0;
      let warrantiesPriceTotal = 0;
      const warrantyEcodes: string[] = [];

      for (const addon of warrantyAddons) {
        warrantiesQtyTotal += addon.qty;

        const matchingWarranty = item.availableWarranties.find(
          (warranty) => warranty.planSku === addon.sku,
        );

        if (matchingWarranty) {
          warrantiesPriceTotal += matchingWarranty.sellingPrice * addon.qty;
          warrantyEcodes.push(matchingWarranty.planEcode);
        }
      }

      productArrayItem.WarrantyEcode = warrantyEcodes.join('|');
      productArrayItem.WarrantyQuantity = warrantiesQtyTotal;
      productArrayItem.WarrantyAveragePrice = warrantiesPriceTotal
        ? warrantiesPriceTotal / warrantiesQtyTotal
        : 0;
    }
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setLoggedIn
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param {string} loggedIn - The Logged In flag as a String
   *
   * @description
   * Sets the Signed In status on the Object. This denotes whether the user has signed in or not.
   */
  public setLoggedIn(loggedIn: string): void {
    this.window.siteData.LoggedIn = loggedIn;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#setCheckout
   * @methodOf dcsg.app.services.service:AnalyticsService
   *
   * @param checkout
   */
  public setCheckout(checkout: Checkout): void {
    // make copy of checkout object... to scrub some data from it
    const checkoutScrubbed = JSON.parse(JSON.stringify(checkout));

    // scrub out all checkout.shopper.address information
    delete checkoutScrubbed.shopper?.address;

    if (checkoutScrubbed.addresses) {
      // scrub out certain info within checkout.addresses objects
      Object.keys(checkoutScrubbed.addresses).forEach((element) => {
        delete checkoutScrubbed.addresses[element].address;
        delete checkoutScrubbed.addresses[element].phone;
        delete checkoutScrubbed.addresses[element].email;
        delete checkoutScrubbed.addresses[element].city;
      });
    }

    const base64Regex = /^[A-Za-z0-9+/]+={0,2}$/;

    if (
      checkoutScrubbed.gift?.recipient &&
      (!base64Regex.test(checkoutScrubbed.gift.recipient) ||
        checkoutScrubbed.gift.recipient.length % 4 !== 0)
    ) {
      checkoutScrubbed.gift.recipient = btoa(checkoutScrubbed.gift.recipient);
    }

    window.siteData.checkout = checkoutScrubbed;
  }

  /**
   * @ngdoc method
   * @name dcsg.app.services.service:AnalyticsService#triggerPostRenderEvent
   * @methodOf dcsg.app.services.service:AnalyticsService
   * @fires dcsg.app.services.service:AnalyticsService#reporting:PagePostRender
   *
   * @description
   * Triggers the Event to notify window.siteData has been updated and passes the Object as data to the Event
   */
  public triggerCheckoutActionEvent(actionType: string) {
    this.window.siteData.EventType = 'CheckoutAction';
    this.window.siteData.ActionType = actionType;

    const additionalDetails = this.getAdditionalDetailsWithDsgUID();

    this.logger.log(
      'AnalyticsService.triggerChecktoutActionEvent -- Event CheckoutAction triggered: ',
      this.scrubbedCheckout(additionalDetails),
    );

    // Trigger the Event and pass along the siteData Object
    this.document.dispatchEvent(this.createCustomEvent('CheckoutAction', additionalDetails));
  }

  public triggerAnalyticsEvent(eventName: string, eventData?: any): void {
    super.triggerAnalyticsEvent(eventName, eventData, this.scrubbedCheckout);
  }

  scrubbedCheckout = (data: any) => {
    const checkoutScrubbed = JSON.parse(JSON.stringify(data.checkout || {}));

    if (checkoutScrubbed.addresses) {
      Object.keys(checkoutScrubbed.addresses).forEach((element) => {
        delete checkoutScrubbed.addresses[element].first_name;
        delete checkoutScrubbed.addresses[element].last_name;
      });
    }
    return { ...data, checkout: checkoutScrubbed };
  };

  public generateLogError(
    errorKey: CheckoutErrorLogKeys | string,
    errorMessage: string,
    errorObject: Error = undefined,
    additionalData: Partial<CheckoutError> = undefined,
    logLevel: LoggerLevel = LoggerLevel.ERROR,
  ) {
    const error: CheckoutError = {
      Error: errorObject,
      ErrorMessage: errorMessage,
      PageType: this.window.siteData?.PageType,
      CheckoutStep: this.window.siteData?.CheckoutStep,
      OrderNumber: this.window.siteData?.checkout?.cart?.external_key,
      CheckoutKey: this.window.siteData?.checkout?.checkout_key,
      CartId: this.window.siteData?.CartID,
      Chain: this.window.siteData?.Chain,
      AppId: this.window.siteData?.appID,
      Authenticated: this.window.siteData?.LoggedIn,
      ReferringPageURL: `${this.document?.referrer}`,
      X_HREF: `${this.window.location?.protocol}//${this.window.location?.host}${this.window.location?.pathname}`, // TODO: Use HttpService instead, but will require a lot of changes to spec files
      DSG_UID: this.cookieService.get('DSG_UID'),
    };

    super.generateLogError(errorKey, errorMessage, error, additionalData, logLevel);
  }

  private createProductArray(checkout: Checkout): any[] {
    const productArray = [];
    const cartItems = checkout.cart.items;
    const availableGroupMap = this.getAvailableGroupMap(checkout.shipping?.available?.groups);
    for (const item of cartItems) {
      const details = {
        ProductBrand: item.manufacturer,
        ProductQty: item.quantity,
        ShippingPaidPerUnit: item.analytics.shipping_paid,
        ProductID: item.ecode,
        ItemDiscountPerUnit: item.analytics.item_discount,
        ProductSKU: item.sku,
        CurrentPricePerUnit: (Number(item.unit_price) - Number(item.analytics.item_discount))
          .toFixed(2)
          .toString(),
        ShippingDiscountPerUnit: item.analytics.shipping_discount,
        DistributedOrderDiscountPerUnit: item.analytics.order_discount,
        TaxPerUnit: item.analytics.tax_paid,
        ListPricePerUnit: item.list_price,
        ProductName: item.description[0],
        FulfillmentMethod: availableGroupMap[item.sku] || null,
        FulfillmentStore: item.bopis_store || '',
        PromotionsApplied: [], // TODO: don't have
        // WarrantyAmount: '0.00',
        // Warranty: 'False'
        // GTGT: 'N'
        // PromotionsApplied: [
        // OrderVsItemLevel:
        // PromoID:
        // Amount:
        // ]
      };

      productArray.push(details);
    }
    return productArray;
  }

  private getAvailableGroupMap(groups: ShipModeGroup[]): { [key: string]: string } {
    const map: { [key: string]: any } = {};

    if (groups) {
      groups.forEach((currGroup) => {
        currGroup.items.forEach((currGroupItem) => {
          map[currGroupItem.sku] = currGroup.group_name;
        });
      });
    }

    return map;
  }

  private setOrderLvlPromoData(orderSummary: any[]): void {
    const promoCodes = [];
    const promoAmmounts = [];

    orderSummary.forEach((item) => {
      if (item.type === 'ORDER_PROMOTION') {
        promoCodes.push(item.promo_id);
        promoAmmounts.push(item.display_amount);
      }
    });

    this.window.siteData.OrderLevelPromoCodes = promoCodes;
    this.window.siteData.OrderLevelPromoAmounts = promoAmmounts;
  }

  private calculateShippingDiscount(cartItems: any[]): string {
    let shippingDiscountAmount = 0;
    for (const item of cartItems) {
      shippingDiscountAmount =
        shippingDiscountAmount + item.quantity * Number(item.analytics.shipping_discount);
    }
    return shippingDiscountAmount.toPrecision(3).toString();
  }

  /**
   * @description
   * Accepts the payment object from the checkout object and returns the different payment types
   *
   * @param payment
   */
  private getPaymentTypesFromPayment(payment: CheckoutPayment): string[] {
    // PaymentType
    const paymentTypes = [];

    // Resiliency types
    const RESILIENCY_TYPES = {
      BILLMELATER: 'BILLMELATER',
      RESILIENCY: 'RESILIENCY',
    };

    // Ensure there is a payment object
    if (typeof payment !== 'undefined') {
      if (typeof payment.payment_processor !== 'undefined') {
        // Check to see if there is a payment type
        if (payment.payment_processor.payment_type) {
          // If payment type is BILLMELATER then we have hit Adyen resiliency and analytics would like us to update the site data
          let paymentType =
            payment.payment_processor.payment_type === RESILIENCY_TYPES.BILLMELATER
              ? RESILIENCY_TYPES.RESILIENCY
              : payment.payment_processor.payment_type;

          // Check to see if the payment type is NOT a resiliency payment
          if (
            paymentType !== RESILIENCY_TYPES.RESILIENCY &&
            typeof payment.payment_processor.saved_payment_indicator === 'string' &&
            payment.payment_processor.saved_payment_indicator.trim() !== ''
          ) {
            // Modify the payment type to include the saved indicator
            paymentType = `${paymentType} (${payment.payment_processor.saved_payment_indicator})`;
          }

          paymentTypes.push(paymentType);
        } else {
          // There is not a payment type, just use the processor
          paymentTypes.push(payment.payment_processor.processor);
        }
      }

      if (typeof payment.gift_cards !== 'undefined') {
        // Iterate the gift cards
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        payment.gift_cards.forEach((element, i) => {
          paymentTypes.push('GiftCard');
        });
      }
    }

    return paymentTypes;
  }

  private getShippingMethods(shippingGroups: ShipModeGroup[]): string[] {
    const selectShipMethods = [];
    if (shippingGroups) {
      shippingGroups.forEach((element, i) => {
        selectShipMethods[i] = element.mode.description;
      });
    }
    return selectShipMethods;
  }

  private getCommerceAddress(addresses: CheckoutAddress): CommerceAddress {
    if (Object.keys(addresses).length !== 0) {
      let billingAddress = addresses.SHIPPINGBILLING;
      if (addresses.BILLING) {
        billingAddress = addresses.BILLING;
      } else if (addresses.SHIPPING) {
        billingAddress = addresses.SHIPPING;
      }

      return billingAddress;
    }
  }
}
